import React, { FC, Fragment, useEffect, memo } from 'react';
import classnames from 'classnames';
import { CSSTransition } from 'react-transition-group';
import { OutsideClick } from '@src/components/OutsideClick';
import { getNotice } from '@src/redux/selectors/notice';
import { disableWindowScroll, enableWindowScroll } from '@src/util/windowScroll';
import { NoticeState } from '@src/redux/reducers/notice/notice.interface';
import { useAppSelector } from '@src/redux';
import { Side } from './enums/side.enum';
import { SideBarProps } from './props';
import './styles.scss';

const TIMEOUT = 350;

export const SideBar: FC<SideBarProps> = memo(
  ({ hasBackDrop, children, isOpen, onOutsideClick, side = Side.RIGHT }) => {
    const noticeData = useAppSelector<NoticeState>(getNotice);

    const doesNoticeExist = !noticeData.isLoading && !!noticeData.notice;

    useEffect(() => {
      if (isOpen) {
        disableWindowScroll();
      } else {
        enableWindowScroll();
      }

      return () => {
        enableWindowScroll();
      };
    }, [isOpen]);

    const hasScrolled = window.scrollY > 0;

    return (
      <Fragment>
        <CSSTransition
          appear
          classNames="side-bar-backdrop"
          in={hasBackDrop && isOpen}
          mountOnEnter
          timeout={TIMEOUT}
          unmountOnExit
        >
          <span className="side-bar-backdrop" />
        </CSSTransition>

        <CSSTransition
          appear
          classNames={classnames(`side-bar--${side}`)}
          in={isOpen}
          mountOnEnter
          timeout={TIMEOUT}
          unmountOnExit
        >
          <OutsideClick addOutsideClickListener={true} onOutsideClick={onOutsideClick}>
            <div
              className={classnames(
                'side-bar',
                doesNoticeExist && !hasScrolled && 'side-bar--small',
                `side-bar--${side}`,
              )}
            >
              <div className="side-bar__container">{children}</div>
            </div>
          </OutsideClick>
        </CSSTransition>
      </Fragment>
    );
  },
);
